/**
 * useInboxNotifications
 *
 * Owns all notification-list state/logic for the Inbox page (the thin
 * `useInbox` orchestrator composes this sub-hook). Ports the notification half
 * of Flutter's MessagesScreen `State`:
 *   - paginated fetch (lib/presentation/screens/messages/messages_screen.dart:142-198)
 *   - the three dropped notification types
 *     (contentAdded / earlyAccessContentAdded / liveStarted), messages_screen.dart:177-185
 *   - dedup append by notification `id` (addAllUnique, list_extensions.dart;
 *     AppNotification == is by id, app_notification.dart:20-26)
 *   - collapse/expand windowing around NOTIFICATIONS_LIST_PAGINATION
 *     (messages_screen.dart:223-235 + the collapsed-refill branch at 143-160)
 *   - the unread-notification predicate (messages_screen.dart:574-585)
 *
 * No UI, no `useBasePage`, no `eventBus.emit` — Task 10's `useInbox` composer
 * owns the lifecycle bridge, the combined badge emit, and the `items` view-model.
 *
 * `currentUserId` is passed in by the composer (the sub-hook never reads Redux
 * directly) so the auth gate stays in the composer and this hook is testable in
 * isolation.
 *
 * Live subscription: the notification half of Flutter's
 * `_subscribeToNotificationsUpdates` (messages_screen.dart:323-373) is now ported
 * here. `getNotifications` starts the live subscription exactly once, on the FIRST
 * page success (Flutter starts it once from `_initData` after the first
 * `_getNotifications` await, messages_screen.dart:95-104), choosing the cursor as
 * the first page's most-recent notification timestamp, or `Date.now()` when that
 * page was empty (messages_screen.dart:324-327 — note this differs from chat,
 * which omits the filter on an empty first page). The change-apply math (the
 * window-preserving add / replace-and-re-sort, messages_screen.dart:410-457),
 * the unread-badge re-check on every change (`_checkNotificationsUpdatesBadge`,
 * satisfied by the `hasUnreadNotification` memo), and the subscription teardown
 * on unmount live alongside here (`applyNotificationChanges` + helpers below).
 *
 * Notification-tap mark-read business logic lives in the colocated
 * `useMarkNotificationRead` hook (composed below, re-exposed unchanged on
 * `UseInboxNotificationsResult`) — never in `InboxPage`, which is pure wiring/UI
 * per `presentation.md` "Page + feature hook split".
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Unsubscribe } from 'firebase/firestore';
import type { PageCursor } from '@/data/types/json';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { RemoteDocumentChangeType } from '@/data/types/remoteDocumentChangeType';
import { FetchNotificationsUseCase } from '@/domain/usecases/notifications/fetchNotificationsUseCase';
import {
  SubscribeToNotificationsUseCase,
  type NotificationChange,
} from '@/domain/usecases/notifications/subscribeToNotificationsUseCase';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useMarkNotificationRead } from './useMarkNotificationRead';

const { NOTIFICATIONS_LIST_PAGINATION } = SERVER_FETCH_CONSTANTS;

/**
 * Notification types that never enter the rendered list.
 * Flutter: messages_screen.dart:177-185 (and the same set on each subscription
 * change at messages_screen.dart:345-352).
 */
const DROPPED_NOTIFICATION_TYPES: ReadonlySet<number> = new Set<number>([
  AppNotificationType.contentAdded,
  AppNotificationType.earlyAccessContentAdded,
  AppNotificationType.liveStarted,
]);

/**
 * The next visible + collapsed lists after applying a single live change.
 * Returned by the pure change-handler helpers so the batch fold can thread the
 * lists through without intermediate `setState` calls (one commit per batch).
 */
interface NextNotificationLists {
  nextVisible: AppNotificationEntity[];
  nextCollapsed: AppNotificationEntity[];
}

/**
 * Window-preserving insert. Flutter: `_onNotificationsSubscriberAdd`,
 * messages_screen.dart:410-434. The three steps run in EXACTLY Flutter's order:
 *   1. window-trim — if the visible list is already at
 *      NOTIFICATIONS_LIST_PAGINATION, move its LAST visible notification to the
 *      FRONT of the collapsed cache and drop it from visible, so the visible
 *      count stays pinned at NOTIFICATIONS_LIST_PAGINATION after the top-insert
 *      (messages_screen.dart:411-417).
 *   2. top-insert — insert the incoming notification at index 0
 *      (messages_screen.dart:419).
 *   3. collapsed-dedup — if the notification already existed in the collapsed
 *      cache, remove it from there (messages_screen.dart:421-427).
 * Both lists are copied (immutable update) so the caller can compare identities.
 */
function applyAddChange(
  visible: AppNotificationEntity[],
  collapsed: AppNotificationEntity[],
  notification: AppNotificationEntity,
): NextNotificationLists {
  let nextVisible = visible;
  let nextCollapsed = collapsed;

  // (1) window-trim (messages_screen.dart:411-417).
  if (nextVisible.length === NOTIFICATIONS_LIST_PAGINATION) {
    const lastVisible = nextVisible[NOTIFICATIONS_LIST_PAGINATION - 1];
    nextCollapsed = [lastVisible, ...nextCollapsed];
    nextVisible = nextVisible.slice(0, NOTIFICATIONS_LIST_PAGINATION - 1);
  }

  // (2) top-insert (messages_screen.dart:419).
  nextVisible = [notification, ...nextVisible];

  // (3) collapsed-dedup (messages_screen.dart:421-427).
  const collapsedIndex = nextCollapsed.findIndex(n => n.id === notification.id);
  if (collapsedIndex !== -1) {
    nextCollapsed = nextCollapsed.slice();
    nextCollapsed.splice(collapsedIndex, 1);
  }

  return { nextVisible, nextCollapsed };
}

/**
 * Replace-and-re-sort. Flutter: `_onNotificationsSubscriberUpdate`,
 * messages_screen.dart:436-457. Two separate steps, matching Flutter:
 *   - index-guard — if `index !== -1`, replace `nextVisible[index]` with the
 *     incoming notification and re-sort the visible list DESC by `info.timestamp`
 *     (messages_screen.dart:440-450).
 *   - collapsed-cleanup — ALWAYS (even when `index === -1`), remove any collapsed
 *     entry whose `id` matches the notification (messages_screen.dart:451-456).
 */
function applyUpdateChange(
  visible: AppNotificationEntity[],
  collapsed: AppNotificationEntity[],
  index: number,
  notification: AppNotificationEntity,
): NextNotificationLists {
  let nextVisible = visible;
  let nextCollapsed = collapsed;

  // index-guard (messages_screen.dart:440-450).
  if (index !== -1) {
    nextVisible = nextVisible.slice();
    nextVisible[index] = notification;
    nextVisible.sort((a, b) => b.info.timestamp - a.info.timestamp);
  }

  // collapsed-cleanup — runs unconditionally (messages_screen.dart:451-456).
  const collapsedIndex = nextCollapsed.findIndex(n => n.id === notification.id);
  if (collapsedIndex !== -1) {
    nextCollapsed = nextCollapsed.slice();
    nextCollapsed.splice(collapsedIndex, 1);
  }

  return { nextVisible, nextCollapsed };
}

export interface UseInboxNotificationsParams {
  /** Resolved by the Task-10 composer (currentUser.firebaseId); owns the auth gate. */
  currentUserId: string | null | undefined;
}

export interface UseInboxNotificationsResult {
  notifications: AppNotificationEntity[];
  collapsedNotifications: AppNotificationEntity[];
  hasMoreNotifications: boolean;
  isFetchingNotifications: boolean;
  /** Any of the FIRST NOTIFICATIONS_LIST_PAGINATION notifications is unread. */
  hasUnreadNotification: boolean;
  getNotifications: () => Promise<void>;
  collapseNotifications: () => void;
  /**
   * Notification-tap mark-read: the `!read` gate + awaited remote write
   * (`push_navigation_service.dart:357-359`) then the flip-on-success-only
   * optimistic update (`messages_screen.dart:665-672`). The page calls this
   * right after dispatching the tap; the unread badge re-derives on its own.
   */
  markNotificationRead: (notification: AppNotificationEntity) => Promise<void>;
}

export function useInboxNotifications(
  params: UseInboxNotificationsParams,
): UseInboxNotificationsResult {
  const { currentUserId } = params;

  const [notifications, setNotifications] = useState<AppNotificationEntity[]>([]);
  const [collapsedNotifications, setCollapsedNotifications] = useState<AppNotificationEntity[]>([]);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
  const [isFetchingNotifications, setIsFetchingNotifications] = useState(false);

  // Refs mirror the live state for the guards inside `getNotifications`, which
  // is a stable callback and would otherwise close over stale values
  // (useWallet pattern). `mostRecentNotificationTimestampRef` mirrors Flutter's
  // `_mostRecentNotificationsDocument` (captured on the first page,
  // messages_screen.dart:186-188) — it is the subscription's `>=` cursor when
  // the first page was non-empty (messages_screen.dart:324-327).
  const isFetchingNotificationsRef = useRef(false);
  const hasMoreNotificationsRef = useRef(true);
  const notificationsRef = useRef<AppNotificationEntity[]>([]);
  const collapsedNotificationsRef = useRef<AppNotificationEntity[]>([]);
  const lastNotificationCursorRef = useRef<PageCursor | undefined>(undefined);
  const mostRecentNotificationTimestampRef = useRef<number | undefined>(undefined);
  // Live-subscription handle (Flutter: `streamSubscriptionNotifications`,
  // messages_screen.dart:339). A non-null value is the idempotency guard — the
  // subscription starts exactly once (messages_screen.dart:323).
  const notificationsUnsubscribeRef = useRef<Unsubscribe | null>(null);
  // Guards the once-only start: Flutter starts the subscription from `_initData`
  // after the FIRST `_getNotifications` await (messages_screen.dart:95-104),
  // never from later `_getNotifications` calls. Set true after the first fetch
  // success so "More updates" / collapsed-refill never re-start it.
  const firstFetchDoneRef = useRef(false);
  const mountedRef = useRef(true);
  const currentUserIdRef = useRef(currentUserId);

  useEffect(() => {
    isFetchingNotificationsRef.current = isFetchingNotifications;
    hasMoreNotificationsRef.current = hasMoreNotifications;
    notificationsRef.current = notifications;
    collapsedNotificationsRef.current = collapsedNotifications;
    currentUserIdRef.current = currentUserId;
  }, [isFetchingNotifications, hasMoreNotifications, notifications, collapsedNotifications, currentUserId]);

  // Single teardown point for the live notifications subscription, mirroring
  // the teardown effect in `useInboxChat`. Tearing down on unmount releases the
  // collection-group listener (Flutter cancels `streamSubscriptionNotifications`
  // on dispose). Idempotent: the ref is nulled so a double-cleanup is a no-op.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (notificationsUnsubscribeRef.current) {
        notificationsUnsubscribeRef.current();
        notificationsUnsubscribeRef.current = null;
      }
    };
  }, []);

  /**
   * Apply one batch of live notification changes (fan-out from the
   * subscription). Flutter: messages_screen.dart:354-371 with helpers
   * `_onNotificationsSubscriberAdd` (410-434) and
   * `_onNotificationsSubscriberUpdate` (436-457).
   *
   * Guarded by `mountedRef`, the batch is folded against the CURRENT lists read
   * from refs (`notificationsRef` / `collapsedNotificationsRef`) so the stable
   * callback never closes over stale state (the `useWallet`/`useInboxChat` ref
   * pattern, mirroring `applyChatChanges` in `useInboxChat`). The resulting
   * next-lists are committed via
   * `setNotifications` / `setCollapsedNotifications`; the existing mirror effect
   * (the `useEffect` that syncs the refs from state, below the ref declarations)
   * re-syncs the refs.
   *
   * Per-change routing (messages_screen.dart:354-368), keyed by `id`:
   *   - `added` + index === -1 → window-preserving insert (`applyAddChange`).
   *   - `added` + index !== -1 → "update with an added flag" (`applyUpdateChange`):
   *     the doc existed locally but was not in the initial query.
   *   - `modified` → `applyUpdateChange`.
   *   - `removed` is never dispatched by Flutter's notifications subscription
   *     (only `added` / `modified`, lines 358-368), so it is ignored.
   *
   * The unread-badge re-check (`_checkNotificationsUpdatesBadge`,
   * messages_screen.dart:370/574-585) is satisfied for free: the
   * `hasUnreadNotification` memo recomputes whenever `notifications` changes.
   */
  const applyNotificationChanges = useCallback((changes: NotificationChange[]) => {
    if (!mountedRef.current) return;
    if (changes.length === 0) return;

    // Fold the batch against the current lists (refs, not closed-over state).
    let nextVisible = notificationsRef.current;
    let nextCollapsed = collapsedNotificationsRef.current;

    for (const change of changes) {
      const notification = change.data;
      // Identity is by `id` (AppNotification == is by id, app_notification.dart:20-26;
      // messages_screen.dart:354-356).
      const index = nextVisible.findIndex(n => n.id === notification.id);

      if (change.type === RemoteDocumentChangeType.added) {
        if (index === -1) {
          const result = applyAddChange(nextVisible, nextCollapsed, notification);
          nextVisible = result.nextVisible;
          nextCollapsed = result.nextCollapsed;
        } else {
          // update was made on the notification but it has an "added" flag
          // because it was not in the initial query (messages_screen.dart:362-364).
          const result = applyUpdateChange(nextVisible, nextCollapsed, index, notification);
          nextVisible = result.nextVisible;
          nextCollapsed = result.nextCollapsed;
        }
      } else if (change.type === RemoteDocumentChangeType.modified) {
        const result = applyUpdateChange(nextVisible, nextCollapsed, index, notification);
        nextVisible = result.nextVisible;
        nextCollapsed = result.nextCollapsed;
      }
      // `removed` is never dispatched by this subscription — ignore it.
    }

    // Commit once after folding the whole batch. The mirror effect re-syncs the
    // refs, and the `hasUnreadNotification` memo recomputes — satisfying the
    // per-change badge re-check (messages_screen.dart:370/574-585).
    if (nextVisible !== notificationsRef.current) {
      notificationsRef.current = nextVisible;
      setNotifications(nextVisible);
    }
    if (nextCollapsed !== collapsedNotificationsRef.current) {
      collapsedNotificationsRef.current = nextCollapsed;
      setCollapsedNotifications(nextCollapsed);
    }
  }, []);

  /**
   * Start the live notifications subscription ONCE. Flutter starts
   * `_subscribeToNotificationsUpdates` a single time from `_initData`
   * (messages_screen.dart:323), so a non-null `notificationsUnsubscribeRef`
   * guards against a second listener. Cursor (messages_screen.dart:324-327):
   *   - non-empty first page → the first page's most-recent notification
   *     `info.timestamp` (`mostRecentNotificationTimestampRef.current`).
   *   - empty first page → `Date.now()` (NOTE: unlike chat, notifications NEVER
   *     subscribes with `undefined`/`0` — it always filters by `>=` a real ms
   *     timestamp).
   */
  const startNotificationsSubscription = useCallback(() => {
    if (notificationsUnsubscribeRef.current) return;
    const owner = currentUserIdRef.current;
    if (!owner) return;
    const cursor = mostRecentNotificationTimestampRef.current ?? Date.now();
    const useCase = new SubscribeToNotificationsUseCase();
    notificationsUnsubscribeRef.current = useCase.execute(
      owner,
      cursor,
      applyNotificationChanges,
    );
  }, [applyNotificationChanges]);

  // Flutter: messages_screen.dart:142-198.
  const getNotifications = useCallback(async () => {
    // (1) Collapsed-refill branch (messages_screen.dart:143-160): move a window
    // back into the visible list and RETURN without fetching.
    const collapsed = collapsedNotificationsRef.current;
    if (collapsed.length > 0) {
      const toInclude = Math.min(collapsed.length, NOTIFICATIONS_LIST_PAGINATION);
      const moved = collapsed.slice(0, toInclude);
      const remaining = collapsed.slice(toInclude);
      collapsedNotificationsRef.current = remaining;
      // Keep `notificationsRef.current` in lockstep with the visible-list mutation
      // (it is the live source of truth `applyNotificationChanges` reads and
      // writes back). Without this, a concurrent live batch in
      // the window before the mirror effect re-syncs would fold against the
      // pre-refill list and clobber this refill.
      const nextVisible = [...notificationsRef.current, ...moved];
      notificationsRef.current = nextVisible;
      setNotifications(nextVisible);
      setCollapsedNotifications(remaining);
      return;
    }

    // (2) Fetch guard (messages_screen.dart:162-164).
    if (isFetchingNotificationsRef.current || !hasMoreNotificationsRef.current) {
      return;
    }

    const owner = currentUserIdRef.current;
    if (!owner) return;

    isFetchingNotificationsRef.current = true;
    setIsFetchingNotifications(true);

    try {
      // (3) Fetch.
      const useCase = new FetchNotificationsUseCase();
      const result = await useCase.execute(
        owner,
        lastNotificationCursorRef.current,
        NOTIFICATIONS_LIST_PAGINATION,
      );

      if (!mountedRef.current) return;

      // Drop the three filtered types (messages_screen.dart:177-185).
      const filtered = result.notifications.filter(
        n => !DROPPED_NOTIFICATION_TYPES.has(n.info.notificationType),
      );

      // (4) First-page most-recent capture (messages_screen.dart:186-188): only
      // when the visible list is empty before this append. Captured SYNCHRONOUSLY
      // (NOT inside the setNotifications updater) so startNotificationsSubscription()
      // below reads the resolved cursor — a useState updater runs later, during the
      // commit, so the ref would still be `undefined` at start time and the cursor
      // would fall through to `Date.now()` even on a non-empty first page. This
      // mirrors the `mostRecentChatTimestampRef` capture in `useInboxChat`.
      // `notificationsRef.current` is the pre-append
      // visible list (the mirror effect keeps it in sync and it has not been
      // mutated for this fetch yet), matching Flutter's `_notifications.isEmpty`.
      if (notificationsRef.current.length === 0 && filtered.length > 0) {
        mostRecentNotificationTimestampRef.current = filtered[0]?.info.timestamp;
      }

      // (5) dedup append by `id` (addAllUnique; AppNotification == is by id).
      setNotifications(prev => {
        // Fold appended ids into `seen` as we build the result so an
        // intra-batch duplicate is also dropped (Flutter addAllUnique checks
        // `contains` against the growing list; list_extensions.dart:5-11).
        const seen = new Set(prev.map(n => n.id));
        const appended: AppNotificationEntity[] = [];
        for (const n of filtered) {
          if (!seen.has(n.id)) {
            seen.add(n.id);
            appended.push(n);
          }
        }
        return [...prev, ...appended];
      });

      // (6) Advance cursor + hasMore.
      lastNotificationCursorRef.current = result.cursor;
      hasMoreNotificationsRef.current = result.hasMore;
      setHasMoreNotifications(result.hasMore);

      // (7) Start the live subscription ONCE, on the first fetch only
      // (messages_screen.dart:95-104 starts it from `_initData` after the first
      // `_getNotifications` await — never from later fetches). The cursor was
      // captured synchronously into `mostRecentNotificationTimestampRef` above
      // (step 4), so it is resolved before this read; the start resolves to
      // `Date.now()` only when the first page was empty
      // (messages_screen.dart:324-327). Runs after the `mountedRef` check above
      // so an unmounted hook never opens a subscription.
      if (!firstFetchDoneRef.current) {
        firstFetchDoneRef.current = true;
        startNotificationsSubscription();
      }
    } catch (error) {
      appLogger.error(
        'Error fetching notifications',
        'useInboxNotifications.getNotifications',
        { error: error as Error },
      );
    } finally {
      isFetchingNotificationsRef.current = false;
      if (mountedRef.current) setIsFetchingNotifications(false);
    }
  }, [startNotificationsSubscription]);

  // Flutter: messages_screen.dart:223-235. Keep the first
  // NOTIFICATIONS_LIST_PAGINATION visible, push the rest into collapsed, and
  // re-arm hasMore so "More updates" can refill from the collapsed cache.
  const collapseNotifications = useCallback(() => {
    const current = notificationsRef.current;
    const visible = current.slice(0, NOTIFICATIONS_LIST_PAGINATION);
    const overflow = current.slice(NOTIFICATIONS_LIST_PAGINATION);

    collapsedNotificationsRef.current = overflow;
    // Keep `notificationsRef.current` in lockstep with the visible-list mutation
    // (the ref is the live truth `applyNotificationChanges` reads and writes
    // back). Without this, a concurrent live batch
    // before the mirror effect re-syncs would fold against the pre-collapse list.
    notificationsRef.current = visible;
    setCollapsedNotifications(overflow);
    setNotifications(visible);

    hasMoreNotificationsRef.current = true;
    setHasMoreNotifications(true);
  }, []);

  // Notification-tap mark-read (the `!read` gate + awaited remote write, then
  // the flip-on-success-only optimistic update). Owned by the colocated
  // `useMarkNotificationRead` hook, which folds the flip over BOTH lists via the
  // refs + setters handed in here so it stays in lockstep with the live
  // subscription's fold. Re-exposed unchanged on the result below.
  const markNotificationRead = useMarkNotificationRead({
    notificationsRef,
    setNotifications,
    collapsedNotificationsRef,
    setCollapsedNotifications,
  });

  // Flutter: messages_screen.dart:574-585 — any of the FIRST
  // NOTIFICATIONS_LIST_PAGINATION notifications is `!read`. Flutter's
  // `NotificationInfo.read` is a non-null bool defaulting to `false`
  // (notification_info.dart:31), so a missing/undefined `read` on web must also
  // count as unread; `!n.info.read` matches that for both `false` and
  // `undefined` (whereas `=== false` would miss the undefined case).
  //
  // This memo IS the web equivalent of Flutter's per-change
  // `_checkNotificationsUpdatesBadge()` re-check (messages_screen.dart:370): it
  // recomputes whenever `notifications` changes, so every live change applied by
  // `applyNotificationChanges` (which commits a new `notifications`) re-derives
  // the badge for free — no separate per-change emit needed.
  const hasUnreadNotification = useMemo(() => {
    const window = notifications.slice(
      0,
      Math.min(NOTIFICATIONS_LIST_PAGINATION, notifications.length),
    );
    return window.some(n => !n.info.read);
  }, [notifications]);

  return {
    notifications,
    collapsedNotifications,
    hasMoreNotifications,
    isFetchingNotifications,
    hasUnreadNotification,
    getNotifications,
    collapseNotifications,
    markNotificationRead,
  };
}
